/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import axiosCall from '../services/axios';
import { isStrEmptyOrNull } from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';
import { useAuthContext } from './AuthContext';
import useAbortController from '../common/hooks/useAbortController';
import dayjs, { Dayjs } from 'dayjs';

type statusAlertType = {
  status: string;
  message: string;
};

type loanDataType = {
  loanNumber: string;
  servicerLoan: string;
  scenarioId: string;
  loanAmount: number;
  loanTerm: number;
  lpi: number;
  dueDate: Dayjs;
};

type lnptDataType = {
  isLoading: boolean;
  upb: number;
  remainingTerm: number;
  intRate: number;
  apparPi: number;
  prinCurtail: number;
  dueDate: number;
  paidOff: boolean;
};

type amortizationScheduleValue = {
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  loadingMessage: string;
  status: string;
  scenarioIdList: MutableRefObject<Array<string>>;

  saveAmortizationScenario: (loanNo: string) => void;
  printAmortizationSchedule: (loanNo: string) => void;
  addAmortizationScenario: () => void;
  deleteAmortizationScenario: (loanNo: string) => void;
  deleteAllScenarios: () => void;
  updatePI: (id: string, origLoanAmt: number, origLoanTerm: number, intRate: number) => void;
  getScenarioById: (string) => [loanDataType, lnptDataType];
  getScenarioSettersById: (string) => [Dispatch<SetStateAction<loanDataType>>, Dispatch<SetStateAction<lnptDataType>>];

  firstPaymentDayjs: Dayjs;
  loanNumber: string;
  servicerLoan: string;
  scenarioId: string;
  loanAmount: number;
  loanTerm: number;
  lpi: number;
  dueDate: Dayjs;
  lnptData: lnptDataType;
  amortScenario1: loanDataType;
  amortScenarioPayment1: lnptDataType;
  amortScenario2: loanDataType;
  amortScenarioPayment2: lnptDataType;
  amortScenario3: loanDataType;
  amortScenarioPayment3: lnptDataType;
  amortScenario4: loanDataType;
  amortScenarioPayment4: lnptDataType;
  amortScenario5: loanDataType;
  amortScenarioPayment5: lnptDataType;
  amortScenario6: loanDataType;
  amortScenarioPayment6: lnptDataType;
  amortScenario7: loanDataType;
  amortScenarioPayment7: lnptDataType;
  amortScenario8: loanDataType;
  amortScenarioPayment8: lnptDataType;
  amortScenario9: loanDataType;
  amortScenarioPayment9: lnptDataType;
};

const AmortizationScheduleContext = createContext<amortizationScheduleValue | null>(null);

const useAmortizationScheduleContext = () => {
  const amortizationScheduleContext = useContext(AmortizationScheduleContext);

  if (amortizationScheduleContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('amortizationScheduleContext must be inside a AmortizationScheduleProvider');
  }
  return amortizationScheduleContext;
};

type amortizationScheduleProviderProps = {
  children: ReactNode;
};

const AmortizationScheduleProvider = ({ children }: amortizationScheduleProviderProps): JSX.Element => {
  const { loanNumber, loanAmount, loanTerm, lpi, servicerLoan, scenarioId, firstPaymentDate } = useLoanContext();
  const firstPaymentDayjs = dayjs(firstPaymentDate * 1000);
  const { reset } = useAbortController();
  const { token } = useAuthContext();

  const [submitStatus, setSubmitStatus] = useState('done');
  const [lnptStatus, setLnptStatus] = useState('loading');
  const [scenarioListStatus, setScenarioListStatus] = useState('loading');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });

  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  const status = useMemo(() => {
    const statuses = [lnptStatus, scenarioListStatus, submitStatus];

    if (statuses.includes('error')) {
      return 'error';
    } else if (statuses.includes('loading')) {
      return 'loading';
    }

    return 'done';
  }, [lnptStatus, scenarioListStatus, submitStatus]);

  // useRef to track whether useEffect functions have fired
  const loadingScenarioList = useRef(false);
  const loadedScenarioList = useRef(false);
  const loadingScenarios = useRef(false);
  const loadedScenarios = useRef(false);

  const saveSuccess = useRef(false);

  // stores the scenarios from the Loan Scenario Groups endpoint
  const scenarioIdList = useRef<Array<string>>([]);

  // Calculate next Due Date from lpi (equals lpi + 1 month)
  const getDueDate = (lpi: number): Dayjs => {
    const dueDate = new Date(lpi * 1000);

    // if it is December, set the date to January of next year
    if (dueDate.getMonth() === 11) {
      dueDate.setMonth(0);
      dueDate.setFullYear(dueDate.getFullYear() + 1);
    } else {
      dueDate.setMonth(dueDate.getMonth() + 1);
    }
    return dayjs(dueDate);
  };

  const getLpiDate = (dueDate: Dayjs): number => {
    let lpiDate = dayjs(dueDate);
    // if it is January, set the date to December of last year
    if (lpiDate.month() === 0) {
      lpiDate.set('month', 11);
      lpiDate.set('year', lpiDate.year() - 1);
    } else {
      lpiDate.set('month', lpiDate.month() + 1);
    }
    return lpiDate.unix();
  };

  // Stores Main Loan data
  const dueDate = useMemo(() => {
    return getDueDate(lpi);
  }, [lpi]);
  const [lnptData, setLnptData] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });

  // Stores Amortization Scenarios data
  const [amortScenario1, setAmortScenario1] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment1, setAmortScenarioPayment1] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario2, setAmortScenario2] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment2, setAmortScenarioPayment2] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario3, setAmortScenario3] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment3, setAmortScenarioPayment3] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario4, setAmortScenario4] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment4, setAmortScenarioPayment4] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario5, setAmortScenario5] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment5, setAmortScenarioPayment5] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario6, setAmortScenario6] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment6, setAmortScenarioPayment6] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario7, setAmortScenario7] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment7, setAmortScenarioPayment7] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario8, setAmortScenario8] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment8, setAmortScenarioPayment8] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });
  const [amortScenario9, setAmortScenario9] = useState({
    loanNumber: '',
    servicerLoan: '',
    scenarioId: '',
    loanAmount: 0,
    loanTerm: 0,
    lpi: 0,
    dueDate: dayjs(),
  });
  const [amortScenarioPayment9, setAmortScenarioPayment9] = useState({
    isLoading: true,
    upb: 0,
    remainingTerm: 0,
    intRate: 0,
    apparPi: 0,
    prinCurtail: 0,
    dueDate: dayjs().unix(),
    paidOff: false,
  });

  const idScenarioMap = new Map<string, [loanDataType, lnptDataType]>()
    .set('Z1', [amortScenario1, amortScenarioPayment1])
    .set('Z2', [amortScenario2, amortScenarioPayment2])
    .set('Z3', [amortScenario3, amortScenarioPayment3])
    .set('Z4', [amortScenario4, amortScenarioPayment4])
    .set('Z5', [amortScenario5, amortScenarioPayment5])
    .set('Z6', [amortScenario6, amortScenarioPayment6])
    .set('Z7', [amortScenario7, amortScenarioPayment7])
    .set('Z8', [amortScenario8, amortScenarioPayment8])
    .set('Z9', [amortScenario9, amortScenarioPayment9]);

  const idScenarioSettersMap = new Map<
    string,
    [Dispatch<SetStateAction<loanDataType>>, Dispatch<SetStateAction<lnptDataType>>]
  >()
    .set('Z1', [setAmortScenario1, setAmortScenarioPayment1])
    .set('Z2', [setAmortScenario2, setAmortScenarioPayment2])
    .set('Z3', [setAmortScenario3, setAmortScenarioPayment3])
    .set('Z4', [setAmortScenario4, setAmortScenarioPayment4])
    .set('Z5', [setAmortScenario5, setAmortScenarioPayment5])
    .set('Z6', [setAmortScenario6, setAmortScenarioPayment6])
    .set('Z7', [setAmortScenario7, setAmortScenarioPayment7])
    .set('Z8', [setAmortScenario8, setAmortScenarioPayment8])
    .set('Z9', [setAmortScenario9, setAmortScenarioPayment9]);

  const saveAmortizationScenario = useCallback(
    async (scenarioId: string) => {
      const [scenario, scenarioPayment] = idScenarioMap.get(scenarioId);
      try {
        // display loading modal
        setSubmitStatus('loading');
        setLoadingMessage('Saving Scenario...');

        if (!loanNumber || !token) return;

        // first, validate the fields to make sure inputs are valid (e.g. Interest Rate > 0%)
        if (scenario.dueDate.isBefore(firstPaymentDayjs, 'day'))
          throw Error(`Due Date must be before the loan's First Payment Date`);
        if (scenario.loanAmount <= 0) throw Error(`Loan Amount must be greater than 0.`);
        if (scenario.loanTerm <= 0) throw Error(`Loan Term must be greater than 0.`);
        if (scenarioPayment.upb <= 0) throw Error(`Principal Balance must be greater than 0.`);
        if (scenarioPayment.remainingTerm <= 0) throw Error(`Remaining Term must be greater than 0.`);
        if (scenarioPayment.intRate <= 0) throw Error(`Interest Rate must be greater than 0.`);
        if (scenarioPayment.apparPi <= 0) throw Error(`P & I Payment must be greater than 0.`);
        if (scenarioPayment.prinCurtail < 0) throw Error(`Additional Principal cannot be negative.`);

        // next, update the loan level fields
        // note: we need to make sure the commitment_nos are blank, or else it will not save.
        const loanRequest = {
          url: `live/loan/${scenario.loanNumber}`,
          method: 'POST',
          data: {
            originalLoanAmount: scenario.loanAmount,
            loanTerm: scenario.loanTerm,
            interestRate: scenarioPayment.intRate,
            firstPaymentDate: getLpiDate(scenario.dueDate),
            buyCommitmentNo: '',
            sellCommitmentNo: '',
          },
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
        };

        let response = await axiosCall(loanRequest, config, headers);
        if (!response)
          throw new Error(`Invalid response for loan ${isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan}`);

        // then, initialize the payment for the due date.
        const initPaymentRequest = {
          url: `${process.env.REACT_APP_PAYMENT_INIT.replace('?', scenario.loanNumber)}${scenario.dueDate.format('YYYYMMDD')}`,
          method: 'POST',
          data: {},
        };

        response = await axiosCall(initPaymentRequest, config, headers);
        if (!response)
          throw new Error(`Invalid response for loan ${isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan}`);

        // finally, save the payment-level fields.
        const paymentRequest = {
          url: `${process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', scenario.loanNumber)}${scenario.dueDate.format('YYYYMMDD')}`,
          method: 'POST',
          data: {
            upb: scenarioPayment.upb,
            remainingTerm: scenarioPayment.remainingTerm,
            intRate: scenarioPayment.intRate,
            apparPi: scenarioPayment.apparPi,
            prinCurtail: scenarioPayment.prinCurtail,
          },
        };

        response = await axiosCall(paymentRequest, config, headers);
        if (!response)
          throw new Error(`Invalid response for loan ${isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan}`);

        setStatusAlert({
          status: 'success',
          message: 'Successfully saved scenario data.',
        });
        saveSuccess.current = true;
      } catch (error) {
        console.error(`Error saving Amortization Scenario data for ${scenario.loanNumber}: ${error.message}`);
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: `Cannot save scenario: ${error.message}`,
        });
        saveSuccess.current = false;
      }
    },
    [loanNumber, token, reset, firstPaymentDayjs, idScenarioMap, servicerLoan],
  );

  const printAmortizationSchedule = useCallback(
    async (loanNo: string) => {
      try {
        if (!loanNumber || !token) return;

        // display loading modal
        setSubmitStatus('loading');
        setLoadingMessage('Printing Document...');

        // if Scenario Loan, send request to update the Loan/LoanPayment fields
        const scenarioId = loanNo.slice(-2);
        if (scenarioId !== '00') {
          await saveAmortizationScenario(scenarioId);
          // Do not print document if saving was unsuccessful
          if (!saveSuccess.current) return;
        }
        setLoadingMessage('Printing Document...');
        // Update to use new docId when Servicing Amortization Schedule document is ready
        const request = {
          url: `${process.env.REACT_APP_DOCPRINTER_LOAN.replace('?', loanNo)}?docId=${process.env.REACT_APP_AMORT_DOCID}`,
          responseType: 'blob' as 'blob',
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
        };

        const response = await axiosCall(request, config, headers);
        if (!response)
          throw new Error(`Invalid response for loan ${isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan}`);
        const pdfBlob = new Blob([response], {
          type: 'application/pdf',
        });

        // Download file on system
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute(
          'download',
          `AmortizationSchedule_${isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan}_${dayjs().format('YYYYMMDD')}`,
        );
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        // hide loading modal
        setSubmitStatus('done');
        setStatusAlert({
          status: 'success',
          message: 'Successfully printed Amortization Schedule.',
        });
      } catch (error) {
        console.error(`Error printing Amortization Schedule for ${loanNo}: ${error.message}`);
        setSubmitStatus('error');
      }
    },
    [loanNumber, saveAmortizationScenario, servicerLoan, token, reset],
  );

  const addAmortizationScenario = async () => {
    try {
      // display loading modal
      setSubmitStatus('loading');
      setLoadingMessage('Adding Scenario...');

      let scenarioNumber: number = -1;
      // iterate through the potential Scenario #s, to find the first one that doesn't exist
      for (let i = 1; i < 10; i++) {
        if (!scenarioIdList.current.includes(`Z${i}`)) {
          scenarioNumber = i;
          break;
        }
      }

      // if full, throw error
      if (scenarioNumber === -1) {
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Max number of Scenarios reached, cannot create scenario.',
        });
        return;
      }

      if (!token || !loanNumber) {
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Error adding Scenario.',
        });
        console.error(`token and/or loanNumber is blank.\ntoken: ${token}\nloanNumber: ${loanNumber}`);
        return;
      }

      // send a request to Create Scenario endpoint
      const url = process.env.REACT_APP_LOAN_ADD_SCENARIO.replace('?', loanNumber).replace('?', `Z${scenarioNumber}`);
      const request = {
        url: url,
        method: 'POST',
        data: {},
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response) throw new Error(`Invalid response from Create Scenario endpoint.`);

      // add new ID to list of existing Scenarios
      scenarioIdList.current = [...scenarioIdList.current, `Z${scenarioNumber}`];
      loadedScenarios.current = false;

      // hide loading modal
      setSubmitStatus('done');
      setStatusAlert({
        status: 'success',
        message: 'Successfully added Scenario.',
      });
    } catch (error) {
      console.error(`Error adding Amortization Scenario: ${error.message}`);
      setSubmitStatus('error');
      setStatusAlert({
        status: 'error',
        message: 'Error creating new Scenario.',
      });
    }
  };

  const deleteAmortizationScenario = useCallback(
    async (id: string) => {
      // display loading modal
      setSubmitStatus('loading');
      setLoadingMessage('Deleting Scenario...');

      try {
        if (!token || !loanNumber || !id) {
          setSubmitStatus('error');
          setStatusAlert({
            status: 'error',
            message: 'Error deleting the Scenario.',
          });
          console.error(
            `token and/or loanNumber and/or id is blank.\ntoken: ${token}\nloanNumber: ${loanNumber}\nid: ${id}`,
          );
          return;
        }

        // send a request to Delete Scenario endpoint
        const url = process.env.REACT_APP_LOAN_DELETE_SCENARIO.replace('?', loanNumber).replace('?', id);
        const request = {
          url: url,
          method: 'POST',
          data: {},
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        if (!response) throw new Error(`Invalid response from Delete Scenario endpoint.`);

        // remove ID from list of existing Scenarios
        scenarioIdList.current = scenarioIdList.current.filter((scenarioId) => {
          return scenarioId !== id;
        });

        // Clear amortScenario and amortScenarioPayment for the scenario
        const [setLoanData, setPaymentData] = idScenarioSettersMap.get(id);
        setLoanData({
          loanNumber: '',
          servicerLoan: '',
          loanAmount: 0,
          loanTerm: 0,
          lpi: 0,
          dueDate: dayjs(),
          scenarioId: '',
        });
        setPaymentData({
          isLoading: true,
          upb: 0,
          remainingTerm: 0,
          intRate: 0,
          apparPi: 0,
          prinCurtail: 0,
          dueDate: dayjs().unix(),
          paidOff: false,
        });

        // hide loading modal
        setSubmitStatus('done');
        setStatusAlert({
          status: 'success',
          message: 'Successfully deleted Scenario.',
        });
      } catch (error) {
        console.error(`Error deleting Amortization Scenario: ${error.message}`);
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Error deleting new Scenario.',
        });
      }
    },
    [loanNumber, reset, token, scenarioIdList, idScenarioSettersMap],
  );

  const deleteAllScenarios = useCallback(() => {
    for (var i in scenarioIdList.current) {
      deleteAmortizationScenario(scenarioIdList.current[i]);
    }
  }, [scenarioIdList, deleteAmortizationScenario]);

  // Sets lnptData
  const getLnptData = useCallback(async () => {
    if (!dueDate.isValid()) return;
    try {
      setLnptStatus('loading');
      setLoadingMessage('Loading...');
      if (!loanNumber || !token) return;
      const request = {
        url: `${process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanNumber)}${dueDate.format('YYYYMMDD')}`,
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response || !response.loanNumber) throw new Error(`Invalid response for loan ${loanNumber}`);
      setLnptData({ ...response, paidOff: response.upb === 0 });
      setLnptStatus('done');
    } catch (error) {
      console.error(`Error getting getLnptData: ${error.message}`);
      setLnptStatus('error');
      setStatusAlert({
        status: 'error',
        message: 'Error getting Loan Payment info.',
      });
      setLnptData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [dueDate, loanNumber, reset, token]);

  // Updates the scenarioIdList with the IDs of active Amortization Scenarios
  const getScenarioIdList = useCallback(async () => {
    // return if scenario list has loaded, or is currently loading.
    if (loadedScenarioList.current || loadingScenarioList.current) return;
    try {
      setScenarioListStatus('loading');
      setLoadingMessage('Loading...');
      if (!loanNumber || !token) return;
      loadingScenarioList.current = true;

      const url = process.env.REACT_APP_LOAN_SCENARIO_GROUP_SERVICING.replace('?', loanNumber);

      const request = {
        url: url,
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response) throw new Error(`Invalid response for loan ${loanNumber} from ${url}`);

      var scenarioIds = [];
      for (var scenario in response) {
        scenarioIds.push(response[scenario]['id']);
      }

      scenarioIdList.current = scenarioIds;
      setScenarioListStatus('done');
      loadedScenarioList.current = true;
      loadingScenarioList.current = false;
    } catch (error) {
      console.error(`Error getting Scenario Groups data: ${error.message}`);
      setScenarioListStatus('error');
      setStatusAlert({
        status: 'error',
        message: 'Error retrieving list of Amortization Scenarios.',
      });
      loadedScenarioList.current = false;
      loadingScenarioList.current = false;
    }
  }, [loanNumber, loadingScenarioList, loadedScenarioList, scenarioIdList, reset, token]);

  // loads data for all active Amortization Scenario Loans, when scenarioIdList is updated
  const loadScenarioLoans = useCallback(async () => {
    // return if scenario data has loaded, is currently loading, or list is empty
    if (loadedScenarios.current || loadingScenarios.current || scenarioIdList.current.length === 0) return;
    try {
      setScenarioListStatus('loading');
      setLoadingMessage('Loading...');
      if (!loanNumber || !token || !loadedScenarioList.current) return;

      loadingScenarios.current = true;
      for (var i in scenarioIdList.current) {
        const scenarioLoanNumber = `?${scenarioIdList.current[i]}`.replace('?', loanNumber.substring(0, 10));

        // send request to /live/loan endpoint
        let url = `live/loan/?`.replace('?', scenarioLoanNumber);
        let request = {
          url: url,
        };
        let config = reset();
        const headers = {
          'Session-Token': token,
        };
        const loanResponse = await axiosCall(request, config, headers);
        if (!loanResponse) throw new Error(`Invalid response for loan ${scenarioLoanNumber} from ${url}`);

        // send request to /live/loan/payment endpoint for the Due Date
        const scenarioDueDate = getDueDate(loanResponse.lpi);
        url = `${process.env.REACT_APP_PAYMENT_DUEDATE.replace('?', loanResponse.loanNumber)}${scenarioDueDate.format('YYYYMMDD')}`;
        request = {
          url: url,
        };
        config = reset();
        const paymentResponse = await axiosCall(request, config, headers);
        if (!paymentResponse) throw new Error(`Invalid response for loan ${scenarioLoanNumber} from ${url}`);

        // if scenario is not loaded, set the corresponding AmortScenario and AmortScenarioPayment
        const [loan] = idScenarioMap.get(`${scenarioIdList.current[i]}`);
        if (isStrEmptyOrNull(loan.loanNumber)) {
          const [setLoanData, setPaymentData] = idScenarioSettersMap.get(`${scenarioIdList.current[i]}`);

          // if loanAmount or remainingTerm is 0, set to base loan's values
          setLoanData({
            loanNumber: loanResponse.loanNumber,
            servicerLoan: loanResponse.servicerLoan,
            scenarioId: loanResponse.scenarioId,
            loanAmount: loanResponse.originalLoanAmount === 0 ? loanAmount : loanResponse.originalLoanAmount,
            loanTerm: loanResponse.loanTerm,
            lpi: loanResponse.lpi,
            dueDate: scenarioDueDate,
          });
          setPaymentData({
            isLoading: false,
            upb: paymentResponse.upb,
            remainingTerm: paymentResponse.remainingTerm === 0 ? lnptData.remainingTerm : paymentResponse.remainingTerm,
            intRate: paymentResponse.intRate,
            apparPi: paymentResponse.apparPi,
            prinCurtail: paymentResponse.prinCurtail,
            dueDate: paymentResponse.dueDate,
            paidOff: paymentResponse.upb === 0,
          });
        }
      }

      setScenarioListStatus('done');

      // do not mark as loaded until the scenarioIdList is initially set
      loadedScenarios.current = true;
      loadingScenarios.current = false;
    } catch (error) {
      console.error(`Error getting Scenario Groups data: ${error.message}`);
      setScenarioListStatus('error');

      setStatusAlert({
        status: 'error',
        message: 'Error loading Amortization Scenario info.',
      });
      loadingScenarios.current = false;
    }
  }, [
    idScenarioMap,
    idScenarioSettersMap,
    loanNumber,
    reset,
    scenarioIdList,
    loadedScenarioList,
    loadingScenarios,
    loadedScenarios,
    token,
    lnptData,
    loanAmount,
  ]);

  // returns the corresponding Scenario fields for the provided id
  const getScenarioById = (id: string) => {
    return idScenarioMap.get(id);
  };

  // returns the corresponding Scenario setters for the provided id
  const getScenarioSettersById = (id: string) => {
    return idScenarioSettersMap.get(id);
  };

  const updatePI = useCallback(
    (id: string, origLoanAmt: number, origLoanTerm: number, intRate: number) => {
      // helper functions
      // i = interest, n = number of payments, p = principal balance
      const PMT = (i: number, n: number, p: number) => {
        return (i * p * Math.pow(1 + i, n)) / (1 - Math.pow(1 + i, n));
      };

      const calculatePI = (origLoanAmt: number, origLoanTerm: number, intRate: number) => {
        return PMT(intRate / 12 / 100, origLoanTerm, -1 * origLoanAmt);
      };

      // get scenario details
      const [loan, payment] = idScenarioMap.get(id);
      const [setLoanData, setPaymentData] = idScenarioSettersMap.get(id);

      const newPI = calculatePI(origLoanAmt, origLoanTerm, intRate);

      setLoanData({
        loanNumber: loan.loanNumber,
        servicerLoan: loan.servicerLoan,
        loanAmount: origLoanAmt,
        loanTerm: origLoanTerm,
        lpi: loan.lpi,
        dueDate: loan.dueDate,
        scenarioId: loan.scenarioId,
      });
      setPaymentData({
        isLoading: false,
        upb: payment.upb,
        remainingTerm: payment.remainingTerm,
        intRate: intRate,
        apparPi: newPI,
        prinCurtail: payment.prinCurtail,
        dueDate: payment.dueDate,
        paidOff: payment.paidOff,
      });
    },
    [idScenarioMap, idScenarioSettersMap],
  );

  useEffect(() => {
    getLnptData();
    getScenarioIdList();
  }, [getLnptData, getScenarioIdList]);

  useEffect(() => {
    loadScenarioLoans();
  }, [loadScenarioLoans, scenarioIdList]);

  return (
    <AmortizationScheduleContext.Provider
      value={{
        statusAlert,
        setStatusAlert,
        loadingMessage,
        status,
        scenarioIdList,

        saveAmortizationScenario,
        printAmortizationSchedule,
        addAmortizationScenario,
        deleteAmortizationScenario,
        deleteAllScenarios,
        getScenarioById,
        getScenarioSettersById,
        updatePI,

        firstPaymentDayjs,
        loanNumber,
        servicerLoan,
        scenarioId,
        loanAmount,
        loanTerm,
        lpi,
        dueDate,
        lnptData,
        amortScenario1,
        amortScenarioPayment1,
        amortScenario2,
        amortScenarioPayment2,
        amortScenario3,
        amortScenarioPayment3,
        amortScenario4,
        amortScenarioPayment4,
        amortScenario5,
        amortScenarioPayment5,
        amortScenario6,
        amortScenarioPayment6,
        amortScenario7,
        amortScenarioPayment7,
        amortScenario8,
        amortScenarioPayment8,
        amortScenario9,
        amortScenarioPayment9,
      }}
    >
      {children}
    </AmortizationScheduleContext.Provider>
  );
};

export { AmortizationScheduleProvider, useAmortizationScheduleContext };
